import { useUserManagementServiceV1 } from "@rw1/api-services";
import { UserManagement_V1 } from "@rw1/auto-generated-clients";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { ASSIGNED_VIA_DIRECT } from "../../../components/AssignedViaCell";
import { useExternalExperienceUsers } from "./useExternalExperienceUsers";

export const USERS_FOR_RELEASE_CHANNEL_QUERY_KEY = "usersForReleaseChannel";

interface UseUsersForReleaseChannelResult {
  usersAccess: Record<string, string[]>; // userId -> groupIds (including ASSIGNED_VIA_DIRECT for direct access)
  isLoading: boolean;
}

interface UseUsersForReleaseChannelOptions {
  enabled?: boolean;
  isExternal?: boolean;
}

export const useUsersForReleaseChannel = (
  releaseChannelId: string | undefined,
  options: UseUsersForReleaseChannelOptions = {},
): UseUsersForReleaseChannelResult => {
  const { enabled = true, isExternal = false } = options;
  const userManagementClient = useUserManagementServiceV1();

  const { companyId } = useParams();
  const { rows: externalRows, loading: loadingExternal } =
    useExternalExperienceUsers({
      releaseChannelId,
      companyId: companyId,
      enabled: isExternal && enabled,
    });

  const { data, isLoading } = useQuery({
    queryKey: [USERS_FOR_RELEASE_CHANNEL_QUERY_KEY, releaseChannelId],
    staleTime: 1000 * 60 * 5,
    enabled: !isExternal && !!releaseChannelId && enabled,
    queryFn: async (): Promise<UserManagement_V1.UserAccessData[]> => {
      if (!releaseChannelId) return [];

      const allResults: UserManagement_V1.UserAccessData[] = [];
      let pageNumber = 0;
      const pageSize = 1000;
      let hasMore = true;

      while (hasMore) {
        const results = await userManagementClient.getUsersForReleaseChannel(
          releaseChannelId,
          pageNumber,
          pageSize,
        );

        if (Array.isArray(results)) {
          allResults.push(...results);
          if (results.length < pageSize) {
            hasMore = false;
          } else {
            pageNumber++;
          }
        } else {
          hasMore = false;
        }
      }

      return allResults;
    },
  });

  const externalUsersAccess = useMemo(() => {
    const accessMap: Record<string, string[]> = {};

    for (const row of externalRows) {
      accessMap[row.userId] ??= [];

      if (row.hasDirectAccess) {
        accessMap[row.userId]!.push(ASSIGNED_VIA_DIRECT);
      }

      for (const source of row.sources) {
        if (source.type === "GROUP") {
          accessMap[row.userId]!.push(source.id);
        }
      }
    }

    return accessMap;
  }, [externalRows]);

  const internalUsersAccess = useMemo(() => {
    const accessMap: Record<string, string[]> = {};
    if (!data) return accessMap;

    for (const item of data) {
      const userId = item.userId;
      if (!userId || typeof userId !== "string") continue;

      accessMap[userId] ??= [];

      const userGroupId = item.userGroupId;
      accessMap[userId].push(
        typeof userGroupId === "string" ? userGroupId : ASSIGNED_VIA_DIRECT,
      );
    }

    return accessMap;
  }, [data]);

  return {
    usersAccess: isExternal ? externalUsersAccess : internalUsersAccess,
    isLoading: isExternal ? loadingExternal : isLoading,
  };
};
